// Step 25 — AI Deal Command Center / Deal Orchestration Workspace.
//
// Single export: AIDealCommandCenter.
//
// This is a COMPOSITION layer over FIVE existing endpoints:
//   - getDealSituationLive(workspaceId)          (Step 12)
//   - listDealActions(workspaceId)               (Step 13)
//   - listContracts(workspaceId)                 (Step 16)
//   - listDealSituationSnapshots(workspaceId)    (Step 12 audit log)
//   - listNotifications(workspaceId)             (Step 14)
//
// Zero new backend endpoints, zero new domain models, zero new
// intelligence. Every value shown is authoritative data one of the
// five endpoints already returned. The Command Center never mutates
// backend state and has no approve / reject / execute / complete /
// cancel / supersede / freeze / sign / send / close buttons.
//
// Existing specialized workspaces remain reachable and authoritative:
//   /deals/:id/actions, /deals/:id/contracts, /deals/:id/contracts/review,
//   /deals/:id/buyers, /deals/:id/risks, /deals/:id/execution,
//   /deals/:id/intelligence, /deals/:id/activity.
//
// Workspace isolation is server-side (each API re-validates via
// `for_user()`). URL ids are never trusted for authorization.
// Failed source → its counter renders "—" (never 0).

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getDealSituationLive,
  listContracts,
  listDealActions,
  listDealSituationSnapshots,
  listNotifications,
} from "../api";
import {
  AIEmpty,
  AIError,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";
import AIAskQadaButton from "../shell/intelligence/AIAskQadaButton";
import AICommandActivity from "../shell/command/AICommandActivity";
import AICommandAttentionChips from "../shell/command/AICommandAttentionChips";
import AICommandConnections from "../shell/command/AICommandConnections";
import AICommandCounts from "../shell/command/AICommandCounts";
import AICommandExecutiveAttention from "../shell/command/AICommandExecutiveAttention";
import AICommandFindings from "../shell/command/AICommandFindings";
import {
  buildCommandAskPrompt,
  buildCommandInvestigationPrompt,
} from "../shell/command/helpers";


function _errMsg(e) {
  if (!e) return null;
  return e?.body?.detail || e.message || "Kunde inte ladda data.";
}

export function AIDealCommandCenter() {
  const { activeWorkspaceId, activeWorkspace } = useAIWorkspace();
  const [situation, setSituation] = useState(null);
  const [actions, setActions] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [snapshots, setSnapshots] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [errors, setErrors] = useState({});

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!activeWorkspaceId) return;

      // Parallel fetch. `allSettled` so any one failure keeps the
      // other four sources readable — the counters + attention list
      // stay truthful for whatever loaded.
      const [sRes, aRes, cRes, snapRes, notifRes] = await Promise.allSettled([
        getDealSituationLive(activeWorkspaceId, { signal }),
        listDealActions(activeWorkspaceId, { limit: 200 }, { signal }),
        listContracts(activeWorkspaceId, { limit: 200 }, { signal }),
        listDealSituationSnapshots(activeWorkspaceId, { limit: 20, signal }),
        listNotifications(activeWorkspaceId, { limit: 20, signal }),
      ]);

      const nextErrors = {};
      if (sRes.status === "fulfilled") setSituation(sRes.value || null);
      else if (sRes.reason?.name !== "AbortError") {
        nextErrors.situation = _errMsg(sRes.reason);
        setSituation(null);
      }
      if (aRes.status === "fulfilled") setActions(aRes.value?.results || []);
      else if (aRes.reason?.name !== "AbortError") {
        nextErrors.actions = _errMsg(aRes.reason);
        setActions(null);
      }
      if (cRes.status === "fulfilled") setContracts(cRes.value?.results || []);
      else if (cRes.reason?.name !== "AbortError") {
        nextErrors.contracts = _errMsg(cRes.reason);
        setContracts(null);
      }
      if (snapRes.status === "fulfilled") {
        setSnapshots(snapRes.value?.results || []);
      } else if (snapRes.reason?.name !== "AbortError") {
        nextErrors.snapshots = _errMsg(snapRes.reason);
        setSnapshots(null);
      }
      if (notifRes.status === "fulfilled") {
        setNotifications(notifRes.value?.results || []);
      } else if (notifRes.reason?.name !== "AbortError") {
        nextErrors.notifications = _errMsg(notifRes.reason);
        setNotifications(null);
      }
      setErrors(nextErrors);
    },
    [activeWorkspaceId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  const combinedError = useMemo(() => {
    const parts = [];
    if (errors.situation) parts.push(`Deal situation: ${errors.situation}`);
    if (errors.actions) parts.push(`Åtgärder: ${errors.actions}`);
    if (errors.contracts) parts.push(`Avtal: ${errors.contracts}`);
    if (errors.snapshots) parts.push(`Snapshots: ${errors.snapshots}`);
    if (errors.notifications) parts.push(`Notiser: ${errors.notifications}`);
    return parts.length === 0 ? null : parts.join(" · ");
  }, [errors]);

  if (!activeWorkspaceId) {
    return <AIEmpty title="Ingen affär vald" />;
  }
  // No full-page spinner — each section already handles its own
  // empty / loading state. The Promise.allSettled fan-out is fast
  // in practice and the counters render "—" while sources are null,
  // so a bare page is never blank.

  const totalSources = 5;
  const okSources = [
    situation, actions, contracts, snapshots, notifications,
  ].filter((x) => x != null).length;

  const askPrompt = buildCommandAskPrompt({
    workspaceName: activeWorkspace?.organisation_name,
  });
  const investigatePrompt = buildCommandInvestigationPrompt({
    workspaceName: activeWorkspace?.organisation_name,
  });

  return (
    <>
      <AIPageHeader
        title="Kommandocentral"
        subtitle={activeWorkspace?.organisation_name}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={askPrompt}
              label="Fråga Qada"
            />
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={investigatePrompt}
              label="Djupare utredning"
            />
          </div>
        }
      />
      <AIError message={combinedError} />

      <div className="qai-command-attention-row">
        <AICommandAttentionChips
          actions={actions || []}
          contracts={contracts || []}
          situation={situation}
          snapshots={snapshots || []}
          notifications={notifications || []}
        />
      </div>

      {okSources < totalSources && (
        <div className="qai-shell-empty-hint" style={{ marginBottom: 12 }}>
          Endast {okSources} av {totalSources} datakällor kunde laddas.
          Räknare för de källor som saknas visas som "—" (inte 0) — vyn
          drar inga slutsatser från saknad data.
        </div>
      )}

      <AICommandCounts
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        snapshots={snapshots || []}
        notifications={notifications || []}
        workspaceId={activeWorkspaceId}
        errors={errors}
      />

      <AICommandExecutiveAttention
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        notifications={notifications || []}
        workspaceId={activeWorkspaceId}
      />

      <AICommandActivity
        snapshots={snapshots || []}
        notifications={notifications || []}
        workspaceId={activeWorkspaceId}
      />

      <AICommandConnections
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        workspaceId={activeWorkspaceId}
      />

      <AICommandFindings
        actions={actions || []}
        contracts={contracts || []}
        situation={situation}
        notifications={notifications || []}
        workspaceId={activeWorkspaceId}
      />

      <AISection title="Specialiserade vyer">
        <div className="qai-command-workspaces">
          <Link
            to={`/deals/${activeWorkspaceId}/intelligence`}
            className="qai-page-section-link"
          >
            Intelligence-vy →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/buyers`}
            className="qai-page-section-link"
          >
            Köpar-intelligence →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/contracts`}
            className="qai-page-section-link"
          >
            Avtals-intelligence →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/execution`}
            className="qai-page-section-link"
          >
            Genomförande & closing readiness →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/risks`}
            className="qai-page-section-link"
          >
            Risker →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/activity`}
            className="qai-page-section-link"
          >
            Aktivitet →
          </Link>
        </div>
      </AISection>

      <AISection title="Mänskligt kontrollerade granskningsvyer">
        <div className="qai-command-review-links">
          <Link
            to={`/deals/${activeWorkspaceId}/actions`}
            className="qai-page-section-link"
          >
            Åtgärder — godkänn / starta / blockera / avsluta →
          </Link>
          <Link
            to={`/deals/${activeWorkspaceId}/contracts/review`}
            className="qai-page-section-link"
          >
            Avtal — godkänn / skicka för signering →
          </Link>
        </div>
        <div className="qai-shell-empty-hint" style={{ marginTop: 10 }}>
          AI-kommandocentralen kan analysera och föreslå nästa steg,
          men kan inte själv genomföra affärens livscykelåtgärder.
          Alla livscykelbeslut sker manuellt via granskningsvyerna
          ovan.
        </div>
      </AISection>
    </>
  );
}

export default AIDealCommandCenter;
